import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL } from "../Config.js";
import styles from "./Analytics.module.css";

const Analytics = () => {
  // Applied filter state (drives data fetching)
  const [summaryType, setSummaryType] = useState(""); // "" | "school" | "item"
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);

  // Modal staging state (only applied on "Apply")
  const [modalOpen, setModalOpen] = useState(false);
  const [tempSummaryType, setTempSummaryType] = useState("");
  const [tempSchools, setTempSchools] = useState([]);
  const [tempItemTypes, setTempItemTypes] = useState([]);

  // Options
  const [schoolList, setSchoolList] = useState([]);
  const [itemTypeList, setItemTypeList] = useState([]);

  // Data
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI
  const [searchTerm, setSearchTerm] = useState("");
  const [optionSearch, setOptionSearch] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedColors, setExpandedColors] = useState(new Set());

  const userData = JSON.parse(sessionStorage.getItem("user"));
  const storeId = userData?.storeId;

  /* ------------------------------------------------------------
     Fetch dropdown lists when modal needs them
     ------------------------------------------------------------ */
  useEffect(() => {
    if (
      tempSummaryType === "school" &&
      storeId &&
      schoolList.length === 0
    ) {
      axios
        .get(`${API_BASE_URL}/user/filter/getSchool`, { params: { storeId } })
        .then((res) => setSchoolList(res.data))
        .catch((err) => console.error("Error fetching schools:", err));
    }
    if (
      tempSummaryType === "item" &&
      storeId &&
      itemTypeList.length === 0
    ) {
      axios
        .get(`${API_BASE_URL}/user/filter/item_type`, { params: { storeId } })
        .then((res) => setItemTypeList(res.data))
        .catch((err) => console.error("Error fetching item types:", err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSummaryType, storeId]);

  /* ------------------------------------------------------------
     Fetch items whenever the APPLIED filters change
     ------------------------------------------------------------ */
  useEffect(() => {
    if (!storeId) return;

    if (summaryType === "school") {
      if (selectedSchools.length === 0) {
        setRawData([]);
        return;
      }
      setLoading(true);
      Promise.all(
        selectedSchools.map((school) =>
          axios.get(`${API_BASE_URL}/user/filter/item_list_school_code`, {
            params: { schoolCode: school, storeId },
          })
        )
      )
        .then((responses) => {
          setRawData(responses.flatMap((r) => r.data || []));
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching school items:", err);
          setLoading(false);
        });
    } else if (summaryType === "item") {
      if (selectedItemTypes.length === 0) {
        setRawData([]);
        return;
      }
      setLoading(true);
      Promise.all(
        selectedItemTypes.map((itemType) =>
          axios.get(`${API_BASE_URL}/user/filter/item_list_type`, {
            params: { itemType, storeId },
          })
        )
      )
        .then((responses) => {
          setRawData(responses.flatMap((r) => r.data || []));
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching item type data:", err);
          setLoading(false);
        });
    }
  }, [summaryType, selectedSchools, selectedItemTypes, storeId]);

  /* ------------------------------------------------------------
     Aggregation: Level 1 -> Color (L2) -> Size (L3)
     ------------------------------------------------------------ */
  const summary = useMemo(() => {
    if (!rawData.length) return [];

    const groupKey = summaryType === "school" ? "itemType" : "itemCategory";

    const buildEmpty = (name) => ({
      name,
      totalStock: 0,
      retailValue: 0,
      wholeSaleValue: 0,
      items: [],
    });

    const accumulate = (acc, item) => {
      const qty = Number(item.quantity) || 0;
      acc.totalStock += qty;
      acc.retailValue += qty * (Number(item.price) || 0);
      acc.wholeSaleValue += qty * (Number(item.wholeSalePrice) || 0);
      acc.items.push(item);
    };

    const topMap = {};
    rawData.forEach((item) => {
      const key = item[groupKey] || "Unknown";
      if (!topMap[key]) topMap[key] = buildEmpty(key);
      accumulate(topMap[key], item);
    });

    Object.values(topMap).forEach((group) => {
      const colorMap = {};
      group.items.forEach((item) => {
        const color = item.itemColor || "Unspecified";
        if (!colorMap[color]) colorMap[color] = buildEmpty(color);
        accumulate(colorMap[color], item);
      });

      Object.values(colorMap).forEach((colorGroup) => {
        const sizeMap = {};
        colorGroup.items.forEach((item) => {
          const size = item.itemSize || "Unspecified";
          if (!sizeMap[size]) sizeMap[size] = buildEmpty(size);
          accumulate(sizeMap[size], item);
        });
        colorGroup.sizes = Object.values(sizeMap).sort(
          (a, b) => b.totalStock - a.totalStock
        );
        delete colorGroup.items;
      });

      group.colors = Object.values(colorMap).sort(
        (a, b) => b.totalStock - a.totalStock
      );
      delete group.items;
    });

    return Object.values(topMap).sort((a, b) => b.totalStock - a.totalStock);
  }, [rawData, summaryType]);

  /* ---------- Search filter ---------- */
  const filteredSummary = useMemo(() => {
    if (!searchTerm.trim()) return summary;
    const term = searchTerm.toLowerCase();
    return summary.filter((row) => row.name.toLowerCase().includes(term));
  }, [summary, searchTerm]);

  /* ---------- Totals ---------- */
  const totals = useMemo(
    () =>
      filteredSummary.reduce(
        (acc, row) => ({
          totalStock: acc.totalStock + row.totalStock,
          retailValue: acc.retailValue + row.retailValue,
          wholeSaleValue: acc.wholeSaleValue + row.wholeSaleValue,
        }),
        { totalStock: 0, retailValue: 0, wholeSaleValue: 0 }
      ),
    [filteredSummary]
  );

  const formatCurrency = (val) =>
    `₹${Number(val).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  /* ---------- Modal handlers ---------- */
  const openModal = () => {
    setTempSummaryType(summaryType);
    setTempSchools(selectedSchools);
    setTempItemTypes(selectedItemTypes);
    setOptionSearch("");
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const applyFilters = () => {
    setSummaryType(tempSummaryType);
    setSelectedSchools(tempSchools);
    setSelectedItemTypes(tempItemTypes);
    setExpandedRows(new Set());
    setExpandedColors(new Set());
    setSearchTerm("");
    setModalOpen(false);
  };

  const handleTempSummaryTypeChange = (type) => {
    setTempSummaryType(type);
    setTempSchools([]);
    setTempItemTypes([]);
    setOptionSearch("");
  };

  /* ---------- Selection helpers (modal) ---------- */
  const tempActiveOptions =
    tempSummaryType === "school" ? schoolList : itemTypeList;
  const tempActiveSelected =
    tempSummaryType === "school" ? tempSchools : tempItemTypes;
  const setTempActiveSelected =
    tempSummaryType === "school" ? setTempSchools : setTempItemTypes;

  const toggleTempSelection = (value) => {
    if (tempActiveSelected.includes(value)) {
      setTempActiveSelected(tempActiveSelected.filter((v) => v !== value));
    } else {
      setTempActiveSelected([...tempActiveSelected, value]);
    }
  };

  const visibleTempOptions = useMemo(() => {
    if (!optionSearch.trim()) return tempActiveOptions;
    const t = optionSearch.toLowerCase();
    return tempActiveOptions.filter((o) => o.toLowerCase().includes(t));
  }, [tempActiveOptions, optionSearch]);

  /* ---------- Row expansion ---------- */
  const toggleRow = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setExpandedColors((colors) => {
          const newColors = new Set(colors);
          Array.from(newColors).forEach((c) => {
            if (c.startsWith(`${key}::`)) newColors.delete(c);
          });
          return newColors;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleColor = (compositeKey) => {
    setExpandedColors((prev) => {
      const next = new Set(prev);
      if (next.has(compositeKey)) next.delete(compositeKey);
      else next.add(compositeKey);
      return next;
    });
  };

  const expandAll = () =>
    setExpandedRows(new Set(filteredSummary.map((r) => r.name)));
  const collapseAll = () => {
    setExpandedRows(new Set());
    setExpandedColors(new Set());
  };

  /* ---------- Applied selection (for chip display) ---------- */
  const activeSelected =
    summaryType === "school" ? selectedSchools : selectedItemTypes;

  const canApply =
    tempSummaryType && tempActiveSelected.length > 0;

  const groupColumnLabel =
    summaryType === "school" ? "Item Type" : "School Name";

  return (
    <div className={styles.page}>
      {/* ---------------- HEADER ---------------- */}
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Analytics Dashboard</h2>
          {summaryType ? (
            <div className={styles.filterChips}>
              <span className={styles.filterBadge}>
                {summaryType === "school" ? "By School" : "By Item"}
              </span>
              <span className={styles.filterDivider}>·</span>
              {activeSelected.slice(0, 3).map((s) => (
                <span key={s} className={styles.appliedPill}>
                  {s}
                </span>
              ))}
              {activeSelected.length > 3 && (
                <span className={styles.appliedPillMore}>
                  +{activeSelected.length - 3} more
                </span>
              )}
            </div>
          ) : (
            <p className={styles.subtitle}>
              Configure filters to view inventory analytics
            </p>
          )}
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={openModal}
        >
          {summaryType ? "Change filters" : "Add filters"}
        </button>
      </header>

      {/* ---------------- STATS ---------------- */}
      {summary.length > 0 && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Stock</div>
            <div className={styles.statValue}>
              {totals.totalStock.toLocaleString("en-IN")}
            </div>
            <div className={styles.statFoot}>units in inventory</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Wholesale Value</div>
            <div className={styles.statValue}>
              {formatCurrency(totals.wholeSaleValue)}
            </div>
            <div className={styles.statFoot}>cost-side total</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Retail Value</div>
            <div className={styles.statValue}>
              {formatCurrency(totals.retailValue)}
            </div>
            <div className={styles.statFoot}>selling-side total</div>
          </div>
        </div>
      )}

      {/* ---------------- TABLE / EMPTY STATE ---------------- */}
      <section className={styles.tableCard}>
        {loading && (
          <div className={styles.centerFill}>
            <div className={styles.spinner}></div>
            <span>Fetching data...</span>
          </div>
        )}

        {!loading && !summaryType && (
          <div className={styles.centerFill}>
            <div className={styles.emptyIcon}>📊</div>
            <h3 className={styles.emptyTitle}>Welcome to Analytics</h3>
            <p className={styles.emptyDesc}>
              Add filters to view inventory summaries grouped by school
              or item type
            </p>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={openModal}
            >
              Add filters
            </button>
          </div>
        )}

        {!loading &&
          summaryType &&
          activeSelected.length > 0 &&
          rawData.length === 0 && (
            <div className={styles.centerFill}>
              <p className={styles.emptyDesc}>
                No inventory data found for this selection.
              </p>
            </div>
          )}

        {!loading && summary.length > 0 && (
          <>
            <div className={styles.tableToolbar}>
              <input
                type="text"
                placeholder={`Filter ${
                  summaryType === "school" ? "item type" : "school"
                }...`}
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className={styles.toolbarRight}>
                <span className={styles.toolbarMeta}>
                  {filteredSummary.length} of {summary.length} rows
                </span>
                <button
                  type="button"
                  className={styles.smallBtn}
                  onClick={expandAll}
                >
                  Expand all
                </button>
                <button
                  type="button"
                  className={styles.smallBtn}
                  onClick={collapseAll}
                >
                  Collapse all
                </button>
              </div>
            </div>

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <colgroup>
                  <col style={{ width: "56px" }} />
                  <col style={{ width: "72px" }} />
                  <col />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "190px" }} />
                  <col style={{ width: "190px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={styles.expandCol}></th>
                    <th>S.No</th>
                    <th>{groupColumnLabel}</th>
                    <th className={styles.right}>Total Stock</th>
                    <th className={styles.right}>Wholesale Value</th>
                    <th className={styles.right}>Retail Value</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSummary.map((row, i) => {
                    const isOpen = expandedRows.has(row.name);
                    return (
                      <React.Fragment key={row.name}>
                        {/* Level 1 */}
                        <tr className={styles.level1Row}>
                          <td>
                            <button
                              type="button"
                              className={`${styles.expandBtn} ${
                                isOpen ? styles.expandBtnOpen : ""
                              }`}
                              onClick={() => toggleRow(row.name)}
                              aria-label="Toggle row"
                            >
                              ▶
                            </button>
                          </td>
                          <td>{i + 1}</td>
                          <td className={styles.nameCell}>{row.name}</td>
                          <td className={styles.right}>
                            {row.totalStock.toLocaleString("en-IN")}
                          </td>
                          <td className={styles.right}>
                            {formatCurrency(row.wholeSaleValue)}
                          </td>
                          <td className={styles.right}>
                            {formatCurrency(row.retailValue)}
                          </td>
                        </tr>

                        {/* Level 2: by color */}
                        {isOpen &&
                          row.colors.map((colorRow) => {
                            const compositeKey = `${row.name}::${colorRow.name}`;
                            const colorOpen =
                              expandedColors.has(compositeKey);
                            return (
                              <React.Fragment key={compositeKey}>
                                <tr className={styles.level2Row}>
                                  <td className={styles.accentL2}>
                                    <button
                                      type="button"
                                      className={`${styles.expandBtn} ${styles.expandBtnSmall} ${
                                        colorOpen
                                          ? styles.expandBtnOpen
                                          : ""
                                      }`}
                                      onClick={() =>
                                        toggleColor(compositeKey)
                                      }
                                      aria-label="Toggle color"
                                    >
                                      ▶
                                    </button>
                                  </td>
                                  <td></td>
                                  <td className={styles.indent1}>
                                    <span className={styles.colorBadge}>
                                      Color
                                    </span>
                                    <span className={styles.level2Name}>
                                      {colorRow.name}
                                    </span>
                                  </td>
                                  <td className={styles.right}>
                                    {colorRow.totalStock.toLocaleString(
                                      "en-IN"
                                    )}
                                  </td>
                                  <td className={styles.right}>
                                    {formatCurrency(colorRow.wholeSaleValue)}
                                  </td>
                                  <td className={styles.right}>
                                    {formatCurrency(colorRow.retailValue)}
                                  </td>
                                </tr>

                                {/* Level 3: by size */}
                                {colorOpen &&
                                  colorRow.sizes.map((sizeRow) => (
                                    <tr
                                      key={`${compositeKey}::${sizeRow.name}`}
                                      className={styles.level3Row}
                                    >
                                      <td className={styles.accentL3}></td>
                                      <td></td>
                                      <td className={styles.indent2}>
                                        <span className={styles.sizeBadge}>
                                          Size
                                        </span>
                                        <span className={styles.level3Name}>
                                          {sizeRow.name}
                                        </span>
                                      </td>
                                      <td className={styles.right}>
                                        {sizeRow.totalStock.toLocaleString(
                                          "en-IN"
                                        )}
                                      </td>
                                      <td className={styles.right}>
                                        {formatCurrency(
                                          sizeRow.wholeSaleValue
                                        )}
                                      </td>
                                      <td className={styles.right}>
                                        {formatCurrency(sizeRow.retailValue)}
                                      </td>
                                    </tr>
                                  ))}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}

                  {filteredSummary.length === 0 && (
                    <tr>
                      <td colSpan={6} className={styles.noMatch}>
                        No matches for "{searchTerm}"
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredSummary.length > 0 && (
                  <tfoot>
                    <tr>
                      <td></td>
                      <td colSpan={2} className={styles.totalLabel}>
                        Total
                      </td>
                      <td className={styles.right}>
                        {totals.totalStock.toLocaleString("en-IN")}
                      </td>
                      <td className={styles.right}>
                        {formatCurrency(totals.wholeSaleValue)}
                      </td>
                      <td className={styles.right}>
                        {formatCurrency(totals.retailValue)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </section>

      {/* ---------------- MODAL ---------------- */}
      {modalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>Configure Analytics</h3>
                <p className={styles.modalSub}>
                  Choose summary type, then pick what to include
                </p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <div className={styles.modalBody}>
              {/* Step 1: summary type */}
              <div className={styles.modalSection}>
                <div className={styles.modalSectionHead}>
                  <span className={styles.stepBadge}>1</span>
                  <span className={styles.modalLabel}>Summary type</span>
                </div>
                <div className={styles.radioGroup}>
                  <label
                    className={`${styles.radioCard} ${
                      tempSummaryType === "school"
                        ? styles.radioCardActive
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="tempSummaryType"
                      value="school"
                      checked={tempSummaryType === "school"}
                      onChange={() =>
                        handleTempSummaryTypeChange("school")
                      }
                    />
                    <div>
                      <span className={styles.radioCardTitle}>
                        By School
                      </span>
                      <span className={styles.radioCardDesc}>
                        Item-type totals per school
                      </span>
                    </div>
                  </label>

                  <label
                    className={`${styles.radioCard} ${
                      tempSummaryType === "item"
                        ? styles.radioCardActive
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="tempSummaryType"
                      value="item"
                      checked={tempSummaryType === "item"}
                      onChange={() =>
                        handleTempSummaryTypeChange("item")
                      }
                    />
                    <div>
                      <span className={styles.radioCardTitle}>By Item</span>
                      <span className={styles.radioCardDesc}>
                        School-wise totals per item type
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Step 2: multi-select */}
              {tempSummaryType && (
                <div className={styles.modalSection}>
                  <div className={styles.modalSectionHead}>
                    <span className={styles.stepBadge}>2</span>
                    <span className={styles.modalLabel}>
                      Select{" "}
                      {tempSummaryType === "school"
                        ? "schools"
                        : "item types"}
                    </span>
                  </div>

                  <div className={styles.multiSelectToolbar}>
                    <input
                      type="text"
                      className={styles.optionSearch}
                      placeholder="Search..."
                      value={optionSearch}
                      onChange={(e) => setOptionSearch(e.target.value)}
                    />
                    <div className={styles.toolbarActions}>
                      <span className={styles.selectionCount}>
                        {tempActiveSelected.length} /{" "}
                        {tempActiveOptions.length}
                      </span>
                      <button
                        type="button"
                        className={styles.smallBtn}
                        onClick={() =>
                          setTempActiveSelected(tempActiveOptions)
                        }
                        disabled={tempActiveOptions.length === 0}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className={styles.smallBtn}
                        onClick={() => setTempActiveSelected([])}
                        disabled={tempActiveSelected.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className={styles.chipsContainer}>
                    {visibleTempOptions.length === 0 && (
                      <span className={styles.noOptions}>
                        {tempActiveOptions.length === 0
                          ? "Loading options..."
                          : "No matches"}
                      </span>
                    )}
                    {visibleTempOptions.map((opt) => {
                      const isActive = tempActiveSelected.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          className={`${styles.chip} ${
                            isActive ? styles.chipActive : ""
                          }`}
                          onClick={() => toggleTempSelection(opt)}
                        >
                          <span className={styles.chipCheck}>
                            {isActive ? "✓" : "+"}
                          </span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <footer className={styles.modalFooter}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={applyFilters}
                disabled={!canApply}
              >
                Apply Filters
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;